/*
 * /board-game-bank — the one door every change goes through.
 *
 *   dispatch(game, cmd, actor, now) → {ok:true, game, entry?} | {ok:false, err, …}
 *
 * The banker's device, the host of a room and the node tests call the same
 * function; a guest phone never changes the game itself — it sends the
 * command to the host, and the host runs it here with actor = {pid}. So the
 * permission rules below ARE the security of a room: a phone can move only
 * its own money, answer only requests addressed to it, and act in a poker
 * hand only on its own turn.
 *
 * Every new entry is checked by replaying the journal with and without it:
 * an entry is refused if it creates a problem that was not there before
 * (money below zero when debt is off, an out player paying, the jackpot
 * going negative). The same test decides whether an old entry may be undone
 * — undoing the rent that paid for a later purchase would leave the buyer
 * below zero, and the page says exactly that instead of doing it.
 */
import * as L from './ledger.js';
import * as P from './poker.js';

const { BANK, POT } = L;

export const OWN_VOID_MS = 5 * 60 * 1000;    // a guest may undo its own entry this long
const KEEP_CLOSED_REQUESTS = 30;

/** Poker settings attached to a new game of kind 'poker'. */
export function newPoker(opts) {
    const o = opts || {};
    const denoms = Array.isArray(o.denoms) && o.denoms.length ? o.denoms.filter((d) => d && d.v > 0).slice(0, 8) : P.DEFAULT_DENOMS.slice();
    const levels = Array.isArray(o.levels) && o.levels.length ? o.levels : P.defaultLevels(o.bb || 20, o.minutes || 15, !!o.ante);
    return {
        denoms,
        unit: P.unitOf(denoms),
        levels,
        timer: { lvl: 0, on: false, t0: 0, acc: 0 },
        button: null,
        forceBtn: null,
        handNo: 0,
        hand: null,
        snaps: [],
        price: o.price > 0 ? o.price : 0,
        last: null,
    };
}

export function createGame(opts, now) {
    const g = L.newGame(Object.assign({}, opts, { now }));
    if (g.kind === 'poker') g.poker = newPoker(opts && opts.poker);
    return g;
}

function isHost(actor) { return !actor || actor.host === true; }
function fail(err, extra) { return Object.assign({ ok: false, err }, extra || {}); }

function activePlayers(g, rp) {
    const r = rp || L.replay(g);
    return g.players.filter((p) => r.joined[p.id] && !r.out[p.id]).map((p) => p.id);
}

function addEntry(g, e, actor, now) {
    e.id = ++g.seq;
    e.t = now;
    e.by = isHost(actor) ? 'host' : actor.pid;
    g.log.push(e);
    return e;
}

/* Append an entry only if it breaks nothing new; otherwise take it back. */
function commit(g, e, actor, now) {
    const before = L.replay(g).problems;
    addEntry(g, e, actor, now);
    const after = L.replay(g);
    const fresh = L.newProblems(before, after.problems);
    if (fresh.length) {
        g.log.pop();
        g.seq--;
        const p = fresh[0];
        if (p.kind === 'neg') {
            const pr = L.replay(g);
            return fail('funds', { pid: p.pid, have: pr.bal[p.pid] || 0 });
        }
        if (p.kind === 'pot') return fail('pot');
        return fail(p.kind === 'out' ? 'out' : 'rule', { pid: p.pid });
    }
    return { ok: true, entry: e };
}

function note(s) {
    return typeof s === 'string' ? s.replace(L.INVISIBLE, '').trim().slice(0, 40) : '';
}

function pay(g, from, to, amount, n, actor, now, extra) {
    if (!L.validAmount(amount)) return fail('amount');
    if (from === to) return fail('same');
    if (!L.isAccount(g, from) || !L.isAccount(g, to)) return fail('account');
    if (from === BANK && to === BANK) return fail('same');
    const e = Object.assign({ type: 'pay', from, to, amount, note: note(n) }, extra || {});
    return commit(g, e, actor, now);
}

/* ── requests ─────────────────────────────────────────────────────────────── */

function pruneRequests(g) {
    const open = g.requests.filter((r) => r.st === 'open');
    const closed = g.requests.filter((r) => r.st !== 'open').slice(-KEEP_CLOSED_REQUESTS);
    g.requests = open.concat(closed).sort((a, b) => a.n - b.n);
}

function newRequest(g, from, to, amount, n, now, group) {
    g.rq = (g.rq | 0) + 1;
    const r = { id: 'r' + g.rq, n: g.rq, from, to, amount, note: note(n), t: now, st: 'open' };
    if (group) r.group = group;
    g.requests.push(r);
    return r;
}

function executeRequest(g, r, actor, now) {
    let res;
    if (r.from === POT && !r.amount) {
        const rp = L.replay(g);
        if (!(rp.pot > 0)) return fail('pot');
        res = commit(g, { type: 'jackpot', pid: r.to, note: r.note }, actor, now);
    } else {
        res = pay(g, r.from, r.to, r.amount, r.note, actor, now, { req: r.id });
    }
    if (!res.ok) return res;
    r.st = 'ok';
    r.eid = res.entry.id;
    r.done = now;
    return res;
}

/* ── the dispatcher ───────────────────────────────────────────────────────── */

export function dispatch(game, cmd, actor, now) {
    if (!cmd || typeof cmd.type !== 'string') return fail('cmd');
    const g = L.clone(game);
    const t = typeof now === 'number' ? now : 0;
    const a = actor || { host: true };
    if (!isHost(a) && !L.player(g, a.pid)) return fail('perm');
    const res = run(g, cmd, a, t);
    if (!res.ok) return res;
    pruneRequests(g);
    g.rev = (g.rev | 0) + 1;
    g.updated = t;
    return Object.assign({}, res, { ok: true, game: g });
}

function run(g, c, a, now) {
    const host = isHost(a);
    const me = host ? null : a.pid;
    const poker = g.kind === 'poker';
    switch (c.type) {
        /* ── money ──────────────────────────────────────────────────────── */
        case 'pay': {
            if (!host && c.from !== me) return fail('perm');
            if (!host && c.from === POT) return fail('perm');
            return pay(g, c.from, c.to, c.amount, c.note, a, now);
        }
        case 'salary': {
            if (!host) return fail('perm');
            if (!(g.salary > 0)) return fail('amount');
            return pay(g, BANK, c.pid, g.salary, 'go', a, now, { tag: 'go' });
        }
        case 'split': {
            if (!L.player(g, c.pid)) return fail('account');
            if (!L.validAmount(c.amount)) return fail('amount');
            const dir = c.dir === 'collect' ? 'collect' : 'pay';
            const rp = L.replay(g);
            if (rp.out[c.pid]) return fail('out', { pid: c.pid });
            const others = activePlayers(g, rp).filter((p) => p !== c.pid);
            if (!others.length) return fail('alone');
            if (!host) {
                if (me !== c.pid) return fail('perm');
                if (dir === 'collect') {
                    // a phone cannot take money from other phones: it asks each of them
                    const group = 'g' + ((g.rq | 0) + 1);
                    for (const o of others) newRequest(g, o, me, c.amount, c.note, now, group);
                    return { ok: true, requested: others.length };
                }
            }
            return commit(g, { type: 'split', pid: c.pid, dir, amount: c.amount, others, note: note(c.note) }, a, now);
        }
        case 'bankrupt': {
            if (!L.player(g, c.pid)) return fail('account');
            if (!host && me !== c.pid) return fail('perm');
            const to = c.to || BANK;
            if (to === c.pid) return fail('same');
            if (to !== BANK && to !== POT && !L.player(g, to)) return fail('account');
            if (to === POT && !g.jackpot) return fail('account');
            if (poker && g.poker.hand && g.poker.hand.order.indexOf(c.pid) >= 0) return fail('in_hand');
            return commit(g, { type: 'bankrupt', pid: c.pid, to }, a, now);
        }
        case 'jackpot': {
            if (!host) return fail('perm');
            if (!g.jackpot) return fail('account');
            if (!L.player(g, c.pid)) return fail('account');
            if (!(L.replay(g).pot > 0)) return fail('pot');
            return commit(g, { type: 'jackpot', pid: c.pid }, a, now);
        }
        case 'revive': {
            if (!host) return fail('perm');
            if (!L.player(g, c.pid)) return fail('account');
            if (!L.replay(g).out[c.pid]) return fail('rule');
            return commit(g, { type: 'revive', pid: c.pid }, a, now);
        }

        /* ── requests ───────────────────────────────────────────────────── */
        case 'request': {
            if (host) return fail('perm');              // the banker just pays
            const from = c.from;
            if (from === me) return fail('same');
            if (from !== BANK && from !== POT && !L.player(g, from)) return fail('account');
            if (from === POT && !g.jackpot) return fail('account');
            const amount = from === POT && !c.amount ? 0 : c.amount;
            if (amount !== 0 && !L.validAmount(amount)) return fail('amount');
            const rp = L.replay(g);
            if (rp.out[me]) return fail('out', { pid: me });
            if (from !== BANK && from !== POT && rp.out[from]) return fail('out', { pid: from });
            const r = newRequest(g, from, me, amount, c.note, now);
            if ((from === BANK || from === POT) && g.autoBank) {
                const res = executeRequest(g, r, { host: true }, now);
                if (!res.ok) { g.requests.pop(); g.rq--; return res; }
                return Object.assign({ request: r.id }, res);
            }
            return { ok: true, request: r.id };
        }
        case 'req_ok': {
            const r = g.requests.find((x) => x.id === c.rid);
            if (!r || r.st !== 'open') return fail('notfound');
            if (!host && me !== r.from) return fail('perm');
            return executeRequest(g, r, a, now);
        }
        case 'req_no': {
            const r = g.requests.find((x) => x.id === c.rid);
            if (!r || r.st !== 'open') return fail('notfound');
            if (!host && me !== r.from && me !== r.to) return fail('perm');
            r.st = !host && me === r.to ? 'cancel' : 'no';
            r.done = now;
            return { ok: true };
        }

        /* ── undo ───────────────────────────────────────────────────────── */
        case 'undo': {
            if (!host) return fail('perm');
            if (poker && g.poker.hand) return handUndo(g);
            const last = lastLive(g);
            if (!last) return fail('nothing');
            return voidEntry(g, last, a, now);
        }
        case 'void': {
            const e = g.log.find((x) => x.id === c.id);
            if (!e || e.void) return fail('notfound');
            if (!host) {
                if (e.by !== me) return fail('perm');
                if (now - e.t > OWN_VOID_MS) return fail('too_old');
            }
            if (poker) {
                if (g.poker.hand) return fail('in_hand');
                if (lastLive(g) !== e) return fail('not_last');
            }
            return voidEntry(g, e, a, now);
        }
        case 'restore': {
            if (!host) return fail('perm');
            const e = g.log.find((x) => x.id === c.id);
            if (!e || !e.void) return fail('notfound');
            if (poker) return fail('not_last');
            const before = L.replay(g).problems;
            const after = L.replay(g, null, new Set([e.id])).problems;
            const fresh = L.newProblems(before, after);
            if (fresh.length) return fail('void_breaks', { pid: fresh[0].pid, at: fresh[0].id });
            delete e.void;
            return { ok: true, entry: e };
        }

        /* ── players ────────────────────────────────────────────────────── */
        case 'player_add': {
            if (!host) return fail('perm');
            if (g.players.length >= L.MAX_PLAYERS) return fail('max_players');
            const i = g.players.length;
            const pid = 'p' + (g.pnext++);
            g.players.push({
                id: pid,
                name: L.cleanName(c.name) || String(i + 1),
                color: c.color || L.COLORS[i % L.COLORS.length],
                token: c.token || L.TOKENS[i % L.TOKENS.length],
                dev: null,
            });
            const amount = L.validAmount(c.amount) ? c.amount : g.start;
            const res = commit(g, { type: 'join', pid, amount }, a, now);
            if (!res.ok) { g.players.pop(); g.pnext--; }
            return Object.assign({ pid }, res);
        }
        case 'player_edit': {
            const p = L.player(g, c.pid);
            if (!p) return fail('account');
            if (!host && me !== c.pid) return fail('perm');
            if (typeof c.name === 'string' && L.cleanName(c.name)) p.name = L.cleanName(c.name);
            if (typeof c.color === 'string' && /^#[0-9a-f]{6}$/i.test(c.color)) p.color = c.color;
            if (typeof c.token === 'string' && c.token.length <= 8) p.token = c.token;
            return { ok: true };
        }
        case 'seat_free': {
            if (!host) return fail('perm');
            const p = L.player(g, c.pid);
            if (!p) return fail('account');
            p.dev = null;
            return { ok: true };
        }
        case 'settings': {
            if (!host) return fail('perm');
            if (typeof c.debt === 'boolean') g.debt = c.debt;
            if (typeof c.autoBank === 'boolean') g.autoBank = c.autoBank;
            if (typeof c.jackpot === 'boolean' && !poker) {
                if (!c.jackpot && L.replay(g).pot > 0) return fail('pot_not_empty');
                g.jackpot = c.jackpot;
            }
            if (typeof c.salary === 'number' && c.salary >= 0 && Math.floor(c.salary) === c.salary && !poker) g.salary = c.salary;
            if (c.cur && typeof c.cur.sym === 'string') g.cur = { sym: c.cur.sym.slice(0, 4), before: !!c.cur.before };
            if (poker && typeof c.price === 'number' && c.price >= 0) g.poker.price = c.price;
            if (poker && Array.isArray(c.levels) && c.levels.length) {
                const lv = c.levels.map(cleanLevel).filter(Boolean);
                if (!lv.length) return fail('amount');
                g.poker.levels = lv;
                g.poker.timer.lvl = Math.min(g.poker.timer.lvl, lv.length - 1);
            }
            return { ok: true };
        }
        case 'end': {
            if (!host) return fail('perm');
            if (poker && g.poker.hand) return fail('in_hand');
            g.ended = now;
            if (poker) g.poker.timer = P.timerPause(g.poker.timer, g.poker.levels, now);
            return { ok: true };
        }
        case 'reopen': {
            if (!host) return fail('perm');
            g.ended = 0;
            return { ok: true };
        }

        /* ── poker ──────────────────────────────────────────────────────── */
        case 'hand_start': return poker ? handStart(g, a, now) : fail('cmd');
        case 'act': return poker ? handAct(g, c, a, now) : fail('cmd');
        case 'award': return poker ? handAward(g, c, a, now) : fail('cmd');
        case 'rebuy': {
            if (!poker || !host) return fail('perm');
            if (!L.player(g, c.pid)) return fail('account');
            if (g.poker.hand && g.poker.hand.order.indexOf(c.pid) >= 0) return fail('in_hand');
            const amount = L.validAmount(c.amount) ? c.amount : g.start;
            return commit(g, { type: 'rebuy', pid: c.pid, amount }, a, now);
        }
        case 'cashout': {
            if (!poker || !host) return fail('perm');
            if (!L.player(g, c.pid)) return fail('account');
            if (g.poker.hand && g.poker.hand.order.indexOf(c.pid) >= 0) return fail('in_hand');
            if (L.replay(g).out[c.pid]) return fail('out', { pid: c.pid });
            return commit(g, { type: 'cashout', pid: c.pid }, a, now);
        }
        case 'button': {
            if (!poker || !host) return fail('perm');
            if (g.poker.hand) return fail('in_hand');
            if (!L.player(g, c.pid)) return fail('account');
            g.poker.forceBtn = c.pid;
            return { ok: true };
        }
        case 'timer': {
            if (!poker || !host) return fail('perm');
            const pk = g.poker;
            if (c.op === 'start') pk.timer = P.timerStart(pk.timer, now);
            else if (c.op === 'pause') pk.timer = P.timerPause(pk.timer, pk.levels, now);
            else if (c.op === 'next') pk.timer = P.timerJump(pk.timer, pk.levels, now, 1);
            else if (c.op === 'prev') pk.timer = P.timerJump(pk.timer, pk.levels, now, -1);
            else if (c.op === 'reset') pk.timer = { lvl: 0, on: false, t0: 0, acc: 0 };
            else return fail('cmd');
            return { ok: true };
        }
        default:
            return fail('cmd');
    }
}

function cleanLevel(l) {
    if (!l) return null;
    const sb = Math.floor(+l.sb), bb = Math.floor(+l.bb), ante = Math.floor(+l.ante || 0), min = +l.min;
    if (!(bb > 0) || !(sb >= 0) || sb > bb || !(ante >= 0) || !(min > 0) || min > 600) return null;
    return { sb, bb, ante, min };
}

function lastLive(g) {
    for (let i = g.log.length - 1; i >= 0; i--) if (!g.log[i].void) return g.log[i];
    return null;
}

function voidEntry(g, e, actor, now) {
    const before = L.replay(g).problems;
    const after = L.replay(g, new Set([e.id])).problems;
    const fresh = L.newProblems(before, after);
    if (fresh.length) return fail('void_breaks', { pid: fresh[0].pid, at: fresh[0].id });
    e.void = { t: now, by: isHost(actor) ? 'host' : actor.pid };
    // a poker hand undone after its award goes back to the showdown
    if (e.type === 'hand' && e.hs && g.poker) {
        g.poker.hand = L.clone(e.hs.hand);
        g.poker.button = e.hs.button;
        g.poker.handNo = e.hs.handNo;
        g.poker.snaps = L.clone(e.hs.snaps || []);
        g.poker.last = null;
        delete e.hs;
        // The hand finished by itself (everybody folded, or nobody had a
        // choice at the showdown): the person's last action was the fold or
        // the call, not an award — undo takes THAT back, in one press.
        if (e.auto) handUndo(g);
    }
    // a request answered by this entry is open again only for the record
    for (const r of g.requests) if (r.eid === e.id) r.st = 'undone';
    return { ok: true, entry: e };
}

/* ── poker commands ───────────────────────────────────────────────────────── */

function pokerStacks(g, rp) {
    const r = rp || L.replay(g);
    const st = {};
    for (const p of g.players) st[p.id] = r.out[p.id] || !r.joined[p.id] ? 0 : (r.bal[p.id] || 0);
    return st;
}

export function blindsNow(g, now) {
    const pk = g.poker;
    const at = P.timerAt(pk.timer, pk.levels, now);
    return Object.assign({ lvl: at.lvl }, pk.levels[at.lvl]);
}

function handStart(g, a, now) {
    if (!isHost(a)) return fail('perm');
    const pk = g.poker;
    if (pk.hand) return fail('in_hand');
    const rp = L.replay(g);
    const seats = g.players.filter((p) => rp.joined[p.id] && !rp.out[p.id]).map((p) => p.id);
    const bl = blindsNow(g, now);
    const res = P.startHand(seats, pokerStacks(g, rp), pk.button, bl, pk.handNo + 1, pk.forceBtn);
    if (!res.ok) return res;
    pk.snaps = [{ hand: null, button: pk.button, handNo: pk.handNo, forceBtn: pk.forceBtn }];
    pk.hand = res.hand;
    pk.button = res.hand.button;
    pk.forceBtn = null;
    pk.handNo++;
    pk.last = null;
    return autoFinish(g, a, now);
}

function handAct(g, c, a, now) {
    const pk = g.poker;
    if (!pk.hand) return fail('nohand');
    if (!isHost(a) && a.pid !== c.pid) return fail('perm');
    const snap = { hand: L.clone(pk.hand), button: pk.button, handNo: pk.handNo, forceBtn: pk.forceBtn };
    const r = P.act(pk.hand, c.pid, c.a, c.to);
    if (!r.ok) { pk.hand = snap.hand; return r; }
    pk.snaps.push(snap);
    if (pk.snaps.length > 200) pk.snaps.splice(1, pk.snaps.length - 200);
    return autoFinish(g, a, now);
}

/* A hand everybody folded, or a showdown where every pot has one possible
   winner, needs no question — it is settled at once. */
function autoFinish(g, a, now) {
    const h = g.poker.hand;
    if (!h || h.street !== 'show') return { ok: true };
    const ps = P.pots(h);
    if (h.done || ps.every((p) => p.elig.length === 1)) return handAward(g, { winners: [], auto: true }, { host: true }, now);
    return { ok: true, showdown: true };
}

function handAward(g, c, a, now) {
    if (!isHost(a)) return fail('perm');
    const pk = g.poker;
    const h = pk.hand;
    if (!h || h.street !== 'show') return fail('nohand');
    const r = P.award(h, c.winners || [], pk.unit);
    if (!r.ok) return r;
    // only the last hand can be undone, so only it keeps its showdown state
    for (const e of g.log) if (e.hs) delete e.hs;
    const e = {
        type: 'hand',
        n: h.n,
        deltas: r.deltas,
        pots: r.pots.map((p) => ({ amount: p.amount, winners: p.winners, shares: p.shares })),
        fold: h.done ? h.winner : null,
        hs: { hand: L.clone(h), button: pk.button, handNo: pk.handNo, snaps: pk.snaps },
    };
    if (c.auto) e.auto = true;
    // an all-folded hand is shown as won by the last player standing
    const res = commit(g, e, a, now);
    if (!res.ok) return res;
    pk.last = { n: h.n, pots: e.pots, fold: e.fold };
    pk.hand = null;
    pk.snaps = [];
    return res;
}

function handUndo(g) {
    const pk = g.poker;
    const s = pk.snaps.pop();
    if (!s) return fail('nothing');
    pk.hand = s.hand;
    pk.button = s.button;
    pk.handNo = s.handNo;
    pk.forceBtn = s.forceBtn || null;
    return { ok: true, undone: 'act' };
}

/**
 * What a guest phone needs: the game without the undo snapshots (they can be
 * large and are only ever used by the host) and without device ids of the
 * other seats (a phone should not learn another phone's id).
 */
export function publicView(g) {
    const v = L.clone(g);
    if (v.poker) v.poker.snaps = [];
    for (const e of v.log) if (e.hs) delete e.hs;
    for (const p of v.players) p.dev = p.dev ? 1 : null;
    return v;
}
